/**
 * Corpse loot — minimal Batch-2 stub. Dead guards carry their sidearm +
 * a small ammo count as lootable items. The proximity-inventory overlay
 * (task 20) reads findAdjacentLoot per frame to decide what to show.
 *
 * No pickup interaction yet — this is data + adjacency only.
 * Headless — no PixiJS.
 */

import type { DisguiseKind } from './disguise';
import type { Guard } from './guard';

/** How many rounds each corpse carries alongside its weapon. */
export const CORPSE_AMMO_COUNT = 6;

/** A carried inventory item — distinct from `LootItem` (a display descriptor for
 *  the corpse overlay). These live in `player.dungeonLoot` once picked up. */
export type ItemKind =
  | 'tool' | 'key' | 'junk' | 'weaponPart' | 'uniform' | 'valuable'
  /** Comms gear. Stealing it is tactical, not financial: it's what the owner
   *  would have used to tell somebody about you (see sim/pickpocket.ts). */
  | 'radio' | 'phone';
export interface Item {
  id: number;
  glyph: string;
  label: string;
  color: number;
  kind: ItemKind;
  /** For a `key`: the id of the lock it opens. */
  lockId?: number;
  /** For a `uniform`: which one — worn to change what observers ask about you. */
  disguise?: DisguiseKind;
  /** For a `valuable`: what it's worth at the end of the run. */
  value?: number;
}

let nextItemId = 1;

/** The mop/broom — the first "dungeon loot" item (found in the janitor closet). */
export function createMop(): Item {
  return { id: nextItemId++, glyph: '/', label: 'Mop', color: 0x9fb0c0, kind: 'junk' };
}

/** Something worth money — what a safe is actually for. */
export function createValuable(label: string, value: number): Item {
  return { id: nextItemId++, glyph: '◆', label, color: 0xffd23f, kind: 'valuable', value };
}

/** A guard's walkie-talkie. Take it and they can't call it in — they have to
 *  go and find someone, like the janitor does. */
export function createRadio(): Item {
  return { id: nextItemId++, glyph: '¶', label: 'Walkie-talkie', color: 0xff8a5a, kind: 'radio' };
}

/** A civilian's phone. Same idea, one step slower: a call instead of a shout. */
export function createPhone(): Item {
  return { id: nextItemId++, glyph: '□', label: 'Cellphone', color: 0x8ad0ff, kind: 'phone' };
}

/** A uniform stripped off a body or lifted from a locker. */
export function createUniform(kind: DisguiseKind): Item {
  const label = { guard: 'Security uniform', janitor: 'Janitor coveralls', worker: 'Office clothes' }[kind];
  const color = { guard: 0xff8a5a, janitor: 0x66d9c0, worker: 0xd8c48a }[kind];
  return { id: nextItemId++, glyph: '∩', label, color, kind: 'uniform', disguise: kind };
}

/** A key that opens exactly the lock with id `lockId`. */
export function createKey(lockId: number, label = 'Key'): Item {
  return { id: nextItemId++, glyph: '⚷', label, color: 0xe6c860, kind: 'key', lockId };
}

export interface LootItem {
  /** Display glyph for the overlay ('⌐' weapons, '≡' ammo, etc.). */
  glyph: string;
  /** Human-readable label. */
  label: string;
  /** Foreground color (0xRRGGBB). */
  color: number;
}

export interface CorpseLoot {
  guardId: number;
  x: number;
  y: number;
  items: LootItem[];
}

const COLOR_WEAPON = 0xffb84a;
const COLOR_AMMO = 0xffd44a;

/** Build the loot list a dead guard carries. Pure — no mutation. */
export function corpseLoot(guard: Guard): LootItem[] {
  return [
    { glyph: '⌐', label: guard.weapon.name, color: COLOR_WEAPON },
    { glyph: '≡', label: `${CORPSE_AMMO_COUNT} rnd`, color: COLOR_AMMO },
  ];
}

/**
 * Every dead guard within 1 Chebyshev tile of the player's current tile
 * (including the player's own tile — you can loot a corpse you're
 * standing on). Live guards are ignored.
 */
export function findAdjacentLoot(
  playerX: number,
  playerY: number,
  guards: readonly Guard[],
): CorpseLoot[] {
  const out: CorpseLoot[] = [];
  for (const g of guards) {
    if (!g.body.dead) continue;
    if (g.looted || g.stashed) continue; // a stashed body is shut inside a prop
    const d = Math.max(Math.abs(g.x - playerX), Math.abs(g.y - playerY));
    if (d > 1) continue;
    out.push({ guardId: g.id, x: g.x, y: g.y, items: corpseLoot(g) });
  }
  return out;
}
